/* eslint-disable no-console */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import Box from 'ui-box';
import moment from 'moment';
import { Button } from 'antd';
import InputField from '../form-helpers/InputField';
import PhoneInputField from '../form-helpers/PhoneInputField';
import InputSelectField from '../form-helpers/InputSelectField';
import { FormDatePicker } from '../form-helpers/FormField';
import LoadingSpinner from '../../common/LoadingSpinner';
import { errorNotification } from '../../../utils/notification-helpers';
import { listProvinces } from '../../../api';
import { putPatienAPICall } from '../../../api/patient';
import AuthService from '../../../services/AuthService'

const auth = new AuthService()
const formItemLayout = {
  labelCol: {
    span: 24,
  },
  wrapperCol: {
    span: 24,
  },
}

function PersonalnformationForm({ onSubmitBtnText, onSubmitForm }) {
  const { t } = useTranslation(['common', 'user-account']);
  const profile = auth.getUserProfile()

  const name = profile.name.split(" ")
  const [, ...lastName] = name;

  const FormSchema = useMemo(
    () =>
      Yup.object().shape({
        firstName: Yup.string().required(t('form.error.required')),
        lastName: Yup.string().required(t('form.error.required')),
        dob: Yup.string().required(t('form.error.required')),
        province: Yup.string().required(t('form.error.required')),
        mobile: Yup.string().required(t('form.error.required')),
      }),
    [t]
  );

  const initialValues = {
    firstName: name[0],
    lastName: lastName.join(" "),
    dob: profile.dob ? moment(profile.dob) : '',
    province: profile.province ?? '',
    mobile: profile.mobile ?? '',
  };

  return (
    <div style={{ width: '100%', textAlign: 'left' }}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={FormSchema}
        onSubmit={async (values) => {
          const dataForm = { ...values };
          dataForm.dob = moment(dataForm.dob).format('YYYY-MM-DD')
          dataForm.name = `${dataForm.firstName} ${dataForm.lastName}`
          const { responseOk, responseJSON } = await putPatienAPICall(dataForm);
          if (responseOk) {
            auth.updateUserAppMetadataInLocalStorage(responseJSON)
            onSubmitForm()
          } else {
            errorNotification(t('form.error.submit'));
          }
        }}
      >
        {({ isSubmitting, isValid, setFieldValue }) => {
          return (
            <Form>
              <fieldset disabled={isSubmitting}>
                <InputField
                  type="text"
                  name="firstName"
                  label={t('user-account:personalInformation.form.firstName.label')}
                  placeholder={t('user-account:personalInformation.form.firstName.placeholder')}
                />
                <InputField
                  type="text"
                  name="lastName"
                  label={t('user-account:personalInformation.form.lastName.label')}
                  placeholder={t('user-account:personalInformation.form.lastName.placeholder')}
                />
                <FormDatePicker
                  type="date"
                  name="dob"
                  defaultValue={moment()}
                  format="YYYY/MM/DD"
                  style={{ width: '100%', padding: '3%' }}
                  formItemLayout={formItemLayout}
                  onChange={(e) => setFieldValue('dob', e)}
                  label={t('user-account:personalInformation.form.dateBirth.label')}
                  placeholder={t('user-account:personalInformation.form.dateBirth.placeholder')}
                  disabledDate={current => {
                    return current && current > moment();
                  }}
                />
                <PhoneInputField
                  name="mobile"
                  label={t('user-account:personalInformation.form.phoneNumber.label')}
                  placeholder={t('user-account:personalInformation.form.phoneNumber.placeholder')}
                />
                <InputSelectField
                  name="province"
                  label={t('user-account:personalInformation.form.province.label')}
                  placeholder={t('user-account:personalInformation.form.province.placeholder')}
                  style={{ width: '100%', padding: '3%' }}
                  formItemLayout={formItemLayout}
                  options={listProvinces()}
                />
                <Box paddingTop={15}>
                  <Button
                    disabled={isSubmitting || !isValid}
                    loading={isSubmitting}
                    htmlType="submit"
                    type="primary"
                    size="large"
                    block
                  >
                    {isSubmitting ? <LoadingSpinner position="full" transparent={false} /> : onSubmitBtnText}
                  </Button>
                </Box>
              </fieldset>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}

PersonalnformationForm.defaultProps = {
  onSubmitBtnText: 'Confirm & Save Changes',
};

const { string, func } = PropTypes;

PersonalnformationForm.propTypes = {
  onSubmitBtnText: string,
  onSubmitForm: func
};

export default PersonalnformationForm;
